import type { VideoGenerationMode } from '@/types/conversation-input';

export type OmniSuggestedVideoMode = Extract<VideoGenerationMode, 'video_edit' | 'video_extend'>;
export type OmniTaskMismatchSource = 'prompt' | 'error';
export type OmniPromptDialogAction = 'continue' | 'switch' | 'dismiss';

export const OMNI_SUGGESTED_VIDEO_MODES: readonly OmniSuggestedVideoMode[] = [
  'video_edit',
  'video_extend',
];

export interface OmniTaskMismatchCopy {
  bannerText: string;
  switchLabel: string;
  dialogTitle: string;
  dialogDescription: string;
  confirmText: string;
  cancelText: string;
  continueText?: string;
}

const PREVIS_GENERATION_PATTERN =
  /白膜|彩色人偶|站位标记|简模|灰模|占位块|分镜和调度|仅用于参考|不是最终角色/;
const SOURCE_VIDEO = '(?:这段|这个|该|原)?视频';
const CASTING_REPLACE_PATTERN = new RegExp(
  [
    '(?:把|将)(?:这段|这个|该|原)?视频(?:里|中|内)?',
    '[^。\\n]{0,16}(?:所有)?(?:角色|人物|人偶|白膜)',
    '[^。\\n]{0,24}(?:替换为|替换成|换成|改成)(?:真实)?(?:真人|人物|演员)',
  ].join(''),
  'g'
);
const EDIT_SOURCE_VIDEO_PATTERN = new RegExp(
  [
    `(?:把|将)${SOURCE_VIDEO}(?:里|中|内)?[^。\\n]{0,24}(?:删除|删掉|去掉|换成|替换成|替换为|替换掉|改成)`,
    `(?:删除|删掉|去掉)(?:(?:这段|这个|该|原)视频|视频(?:里|中|内))`,
    `(?:给|向|往)${SOURCE_VIDEO}(?:里|中|内)?(?:加上|添加)`,
    `(?:加上|添加)[^。\\n]{0,16}到${SOURCE_VIDEO}`,
    `(?:编辑|剪辑)(?:这段|这个|该|原)视频`,
    `对(?:这段|这个|该|原)?视频\\s*(?:进行|做|加以)?\\s*编辑`,
    `视频编辑`,
  ].join('|')
);
const EXTEND_PROMPT_PATTERN = /延长|续写|向后延长|向前延长/;
const TASK_TYPE_CONSTRAINT = 'invalidparameter.tasktypeconstraint';

function promptLooksLikeSourceVideoEdit(prompt: string): boolean {
  const withoutReferenceMentions = prompt.replace(/白膜视频|参考视频/g, '');
  const withoutCasting = withoutReferenceMentions.replace(CASTING_REPLACE_PATTERN, '');
  return EDIT_SOURCE_VIDEO_PATTERN.test(withoutCasting);
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function includesMode(
  allowedVideoModes: readonly string[] | undefined,
  mode: OmniSuggestedVideoMode
): boolean {
  if (!allowedVideoModes) return true;
  if (allowedVideoModes.length === 0) return false;
  if (!allowedVideoModes.includes('reference')) return false;
  return allowedVideoModes.includes(mode);
}

function pickAllowedMode(
  preferred: OmniSuggestedVideoMode,
  allowedVideoModes: readonly string[] | undefined
): OmniSuggestedVideoMode | null {
  if (includesMode(allowedVideoModes, preferred)) return preferred;
  const fallback: OmniSuggestedVideoMode =
    preferred === 'video_edit' ? 'video_extend' : 'video_edit';
  return includesMode(allowedVideoModes, fallback) ? fallback : null;
}

export function detectOmniReferencePromptMismatch(params: {
  videoMode?: string | null;
  allowedVideoModes?: readonly string[];
  prompt: string;
  hasReferenceVideo: boolean;
}): OmniSuggestedVideoMode | null {
  if (params.videoMode !== 'reference') return null;
  if (!params.hasReferenceVideo) return null;

  const prompt = params.prompt ?? '';
  if (PREVIS_GENERATION_PATTERN.test(prompt)) return null;

  const hasEditIntent = promptLooksLikeSourceVideoEdit(prompt);
  const hasExtendIntent = EXTEND_PROMPT_PATTERN.test(prompt);
  if (!hasEditIntent && !hasExtendIntent) return null;

  if (hasEditIntent) {
    return pickAllowedMode('video_edit', params.allowedVideoModes);
  }
  return pickAllowedMode('video_extend', params.allowedVideoModes);
}

function collectPayloadStrings(value: unknown, bucket: string[], depth = 0): void {
  if (depth > 6 || value == null) return;
  if (typeof value === 'string' || typeof value === 'number') {
    const text = String(value).trim();
    if (text) bucket.push(text);
    return;
  }
  if (Array.isArray(value)) {
    for (const item of value) collectPayloadStrings(item, bucket, depth + 1);
    return;
  }
  if (typeof value === 'object') {
    for (const nested of Object.values(value as Record<string, unknown>)) {
      collectPayloadStrings(nested, bucket, depth + 1);
    }
  }
}

function isTaskTypeConstraint(text: string): boolean {
  const normalized = text.toLowerCase();
  return (
    normalized.includes(TASK_TYPE_CONSTRAINT) ||
    normalized.includes('identified your task as video editing') ||
    normalized.includes('identified your task as video extension') ||
    text.includes('判定为视频编辑或视频延长')
  );
}

export function detectOmniTaskTypeConstraintMismatch(params: {
  errorCode?: string | null;
  errorRaw?: string | null;
  errorMessage?: string | null;
  payload?: unknown;
  allowedVideoModes?: readonly string[];
}): OmniSuggestedVideoMode | null {
  const chunks = [
    asString(params.errorCode),
    asString(params.errorRaw),
    asString(params.errorMessage),
  ];
  collectPayloadStrings(params.payload, chunks);
  const combined = chunks.join('\n');
  if (!isTaskTypeConstraint(combined)) return null;

  const lower = combined.toLowerCase();
  const looksLikeEdit =
    lower.includes('duration') && lower.includes('-1') ? true : lower.includes('video editing');
  const looksLikeExtend = lower.includes('video extension') || lower.includes('video extend');

  if (looksLikeEdit) {
    return pickAllowedMode('video_edit', params.allowedVideoModes);
  }
  if (looksLikeExtend || (lower.includes('ratio') && lower.includes('adaptive'))) {
    return pickAllowedMode('video_extend', params.allowedVideoModes);
  }
  return pickAllowedMode('video_edit', params.allowedVideoModes);
}

export function getOmniTaskMismatchCopy(
  suggestedMode: OmniSuggestedVideoMode,
  source: OmniTaskMismatchSource
): OmniTaskMismatchCopy {
  const modeLabel = suggestedMode === 'video_edit' ? '视频编辑' : '视频延长';
  const switchLabel = `切换到${modeLabel}`;
  const bannerText = `提示词更像${modeLabel}，继续用参考生视频可能失败。`;
  const dialogTitle = source === 'error' ? `请切换到${modeLabel}` : `提示词更像${modeLabel}`;
  const dialogDescription =
    source === 'error'
      ? `模型把这次任务判定为${modeLabel}。参考生视频的比例和时长与该任务不兼容，请切换到「${modeLabel}」后再试。`
      : `当前是参考生视频，但提示词更像对原视频做${modeLabel === '视频编辑' ? '编辑' : '延长'}。可以切换到「${modeLabel}」，也可以继续用参考生视频。`;

  return {
    bannerText,
    switchLabel,
    dialogTitle,
    dialogDescription,
    confirmText: switchLabel,
    cancelText: '返回修改',
    ...(source === 'prompt' ? { continueText: '继续' } : {}),
  };
}

export interface OmniMismatchDialogButtons {
  cancelText: string;
  secondaryText?: string;
  confirmText: string;
  confirmAction: OmniPromptDialogAction;
  secondaryAction?: OmniPromptDialogAction;
}

/** ConfirmDialog 顺序是取消 | 次要 | 主按钮。有「继续」时主按钮放继续，切换居中。 */
export function getOmniMismatchDialogButtons(
  copy: OmniTaskMismatchCopy
): OmniMismatchDialogButtons {
  if (copy.continueText) {
    return {
      cancelText: copy.cancelText,
      secondaryText: copy.confirmText,
      confirmText: copy.continueText,
      confirmAction: 'continue',
      secondaryAction: 'switch',
    };
  }
  return {
    cancelText: copy.cancelText,
    confirmText: copy.confirmText,
    confirmAction: 'switch',
  };
}
